import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  BookOpen,
  Award,
  ExternalLink,
  CheckCircle,
  Clock,
  Building2
} from 'lucide-react';

export const EmployeeTraining = () => {
  const { employee, api } = useEmployeeAuth();
  const [trainingData, setTrainingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTraining();
  }, []);

  const fetchTraining = async () => {
    try {
      const response = await api.get('/api/employee-portal/available-training');
      setTrainingData(response.data);
    } catch (error) {
      console.error('Error fetching training:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'certification':
        return 'bg-green-100 text-green-700';
      case 'specialized':
        return 'bg-purple-100 text-purple-700';
      case 'training':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Training & Certifications</h1>
        <p className="text-gray-500 text-sm">Level up your skills and earn more</p>
      </div>

      {/* Current Certifications */}
      {employee.certifications && employee.certifications.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-semibold">Your Certifications</h2>
          </div>
          <div className="space-y-2">
            {employee.certifications.map((cert, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">{cert}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Training Records */}
      {employee.training_records && employee.training_records.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-semibold">Training History</h2>
          </div>
          <div className="space-y-3">
            {employee.training_records.map((record, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium">{record.name}</p>
                    <p className="text-sm text-gray-500">{record.provider}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(record.type)}`}>
                    {record.type}
                  </span>
                </div>
                {record.date_obtained && (
                  <p className="text-xs text-gray-500">
                    Obtained: {new Date(record.date_obtained).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Available Training */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-[#C6A87C]" />
          <h2 className="font-semibold">Available Training</h2>
        </div>
        
        <p className="text-sm text-gray-500 mb-4">
          Complete these to advance your career and increase your pay tier
        </p>

        <div className="space-y-4">
          {trainingData?.training_opportunities?.map((training, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className="border border-gray-100 rounded-xl p-4 hover:border-[#C6A87C]/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="font-semibold">{training.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">{training.provider}</span>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${getTypeColor(training.type)}`}>
                  {training.type}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{training.description}</p>
              <a
                href={training.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#C6A87C] text-sm font-medium hover:underline"
              >
                Learn More
                <ExternalLink className="w-4 h-4" />
              </a>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Note */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-[#C6A87C]/10 rounded-2xl p-4"
      >
        <p className="text-sm text-[#C6A87C]">
          <strong>Tip:</strong> {trainingData?.note || 'Completing certifications helps you advance to higher pay tiers. Contact your supervisor to schedule training.'}
        </p>
      </motion.div>
    </div>
  );
};

export default EmployeeTraining;
